import { DateTime } from "luxon";
import { z } from "zod/v4-mini";

import {
  ConditionType,
  conditionTypeSchema,
  MedicationSchema,
  medicationSchemeSchema,
} from "@/models/ConditionModel.ts";
import { type PumpDeviceBrand, pumpDeviceBrandSchema } from "@/models/MedicalDeviceModel.ts";
import {
  termDateInFutureIfGestational,
  termDateIsValidIfGestational,
} from "@/uiKit/organisms/PatientCreationAndRenewal/termDateRules.ts";
import { cz } from "@/utils/zodUtils.ts";

export type NursePreInclusionInfo = {
  doctorId: string;
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
  helperPhone?: string;
  birthDate?: string;
  diabetesType?: ConditionType;
  insulinScheme?: MedicationSchema;
  pump?: PumpDeviceBrand;
  termDate?: string;
  inclusionReason: string;
};

const cannotBeType12AndNoInsulin = (data: NursePreInclusionInfo) => {
  if (data.diabetesType === "type1" || data.diabetesType === "type2") {
    return data.insulinScheme !== "noInsulin";
  }
  return true;
};

const cannotBeGestationalAndUsePump = (data: NursePreInclusionInfo) => {
  if (data.diabetesType === "gestational") {
    return data.insulinScheme !== "insulinPump";
  }
  return true;
};

const requiredTermDateIfGestational = (data: NursePreInclusionInfo) => {
  if (data.diabetesType === "gestational") {
    return !!data.termDate;
  }
  return true;
};

export const nursePreInclusionInfoSchema = z
  .object({
    doctorId: z.string("form.required"),
    givenName: z.string("form.required").check(z.minLength(1)),
    familyName: z.string("form.required").check(z.minLength(1)),
    email: z.email("form.email.invalid"),
    phone: cz.phone({
      string: "form.required",
      refine: "form.phone.invalid",
    }),
    helperPhone: z.optional(
      cz.phone({
        string: "form.required",
        refine: "form.phone.invalid",
      }),
    ),
    birthDate: z.iso.date("form.date.invalid").check(cz.validation.dateBefore(DateTime.now(), "form.date.future")),

    diabetesType: conditionTypeSchema,
    insulinScheme: medicationSchemeSchema,
    pump: z.optional(pumpDeviceBrandSchema),
    // Validated by the gestational-only refinements below: a hidden leftover must not block.
    termDate: z.optional(z.string()),
    inclusionReason: z.string("form.required").check(z.minLength(1)),
  })
  .check(
    z.refine(cannotBeType12AndNoInsulin, {
      path: ["insulinScheme"],
      error: "form.required",
    }),
    z.refine(cannotBeGestationalAndUsePump, {
      path: ["insulinScheme"],
      error: "form.required",
    }),
    z.refine(requiredTermDateIfGestational, {
      path: ["termDate"],
      error: "form.required",
    }),
    z.refine(termDateIsValidIfGestational, {
      path: ["termDate"],
      error: "form.date.invalid",
    }),
    z.refine(termDateInFutureIfGestational, {
      path: ["termDate"],
      error: "form.date.past",
    }),
  );
